import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Upload, ImageIcon } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

/**
 * ProgressPhotos — monthly visual log. Uploads to Emergent object storage
 * via `/api/track/progress-photo`, streams downloads via signed backend proxy,
 * groups thumbnails by month for a scannable "before/after" story.
 */
export function ProgressPhotos() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState("");
  const fileInputRef = useRef(null);
  // In-memory blob URLs to render <img> without leaking Authorization
  const [previews, setPreviews] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/track/progress-photos");
      setPhotos(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Fetch each photo as a blob so <img> can render with auth
  useEffect(() => {
    let cancelled = false;
    const load_previews = async () => {
      const next = { ...previews };
      let changed = false;
      for (const p of photos) {
        if (next[p.id]) continue;
        try {
          const res = await api.get(`/track/progress-photo/${p.id}/download`, { responseType: "blob" });
          if (cancelled) return;
          next[p.id] = URL.createObjectURL(res.data);
          changed = true;
        } catch { /* ignore per-item */ }
      }
      if (changed && !cancelled) setPreviews(next);
    };
    load_previews();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.map((p) => p.id).join("|")]);

  useEffect(() => () => {
    Object.values(previews).forEach((u) => URL.revokeObjectURL(u));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Max 8MB per photo."); return;
    }
    if (!/^image\//.test(file.type)) {
      toast.error("Images only."); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (note) fd.append("note", note);
      await api.post("/track/progress-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded.");
      setNote("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/track/progress-photo/${id}`);
      setPhotos((ps) => ps.filter((p) => p.id !== id));
      if (previews[id]) URL.revokeObjectURL(previews[id]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  // Group by YYYY-MM
  const grouped = photos.reduce((acc, p) => {
    const ym = (p.taken_at || p.created_at || "").slice(0, 7);
    (acc[ym] = acc[ym] || []).push(p);
    return acc;
  }, {});
  const months = Object.keys(grouped).sort((a, b) => (a < b ? 1 : -1));

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="progress-photos-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><ImageIcon size={12} /> Progress Photos</div>
          <h2 className="wordmark mt-2 text-2xl uppercase">Month-by-month visual proof.</h2>
          <p className="mt-1 text-xs text-muted-foreground max-w-md">Same light, same time of day, same pose. The trend is what matters.</p>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-end gap-3">
        <label className="text-xs flex-1 min-w-[220px]">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Optional note</span>
          <input value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Front · morning · fasted"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            data-testid="progress-photo-note" />
        </label>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={onFile} hidden data-testid="progress-photo-input" />
        <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
          className="btn-primary btn-sm disabled:opacity-60" data-testid="progress-photo-upload-btn">
          <span className="inline-flex items-center gap-1.5"><Upload size={14} /> {uploading ? "Uploading…" : "Upload photo"}</span>
        </button>
      </div>

      <div className="mt-6 space-y-6">
        {loading && <div className="text-xs text-muted-foreground uppercase tracking-widest">Loading…</div>}
        {!loading && photos.length === 0 && (
          <div className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-6 text-center">
            No progress photos yet. Upload your first one to lock in a baseline.
          </div>
        )}
        {months.map((ym) => (
          <div key={ym}>
            <div className="label-eyebrow">{new Date(ym + "-01").toLocaleDateString(undefined, { year: "numeric", month: "long" })}</div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {grouped[ym].map((p) => (
                <div key={p.id} className="relative rounded-md overflow-hidden border border-border group" data-testid={`progress-photo-${p.id}`}>
                  {previews[p.id] ? (
                    <img src={previews[p.id]} alt={p.note || "progress"} className="w-full h-40 object-cover" />
                  ) : (
                    <div className="w-full h-40 bg-muted animate-pulse" />
                  )}
                  <button onClick={() => del(p.id)} title="Delete"
                    className="absolute top-2 right-2 bg-background/80 hover:bg-destructive hover:text-destructive-foreground rounded-md p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`progress-photo-delete-${p.id}`}>
                    <Trash2 size={14} />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background/95 to-transparent p-2">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{(p.taken_at || p.created_at || "").slice(0, 10)}</div>
                    {p.note && <div className="text-xs mt-0.5 line-clamp-1">{p.note}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
